import api from "../services/authService";

/**
 * Central eFolder upload client (dms_backend spec #2743, sections 3.3-3.5).
 *
 * The bytes still travel through `aiZipUploadService.uploadZipServerSide`
 * (initiate -> parts -> complete); this module covers what happens after:
 * the batch list the page polls, the review actions on a job the backend
 * could not match on its own, and the per-matter freshness rows.
 *
 * Every route here answers 404 while the backend flag
 * `EFOLDER_CENTRAL_UPLOAD_ENABLED` is off, and the UI itself ships dark
 * behind `REACT_APP_EFOLDER_UPLOAD` (see `efolderUploadEnabled`).
 *
 * Privacy: job payloads carry zip names and matter names. Nothing in this
 * module logs a payload, and callers must not either.
 */

const TRUE_VALUES = new Set(["1", "true", "yes", "on"]);

// Build-time flag, read on every call so a test can flip it without a
// module reload. Default off: with it off nothing new renders anywhere.
export const efolderUploadEnabled = () =>
  TRUE_VALUES.has(String(process.env.REACT_APP_EFOLDER_UPLOAD || "").trim().toLowerCase());

// The one candidate a job is "about": the matter it already has (after
// confirm or reassign), else the primary matter the server proposed, else
// the strongest candidate. The row's match cell and the confirm modal's
// preselected radio both use this, so what the table shows is what a
// one-click Confirm files into.
export const preferredCandidate = (job) => {
  const candidates = job?.match?.candidates || [];
  if (job?.matter_id) {
    // Once assigned, only the assigned matter counts. A matter chosen by
    // search is often not among the candidates; then there is no candidate
    // to describe and the caller falls back to the job's matter fields.
    return candidates.find((c) => String(c.matter_id) === String(job.matter_id)) || null;
  }
  return candidates.find((c) => c.matter_summary?.primary) || candidates[0] || null;
};

// Mirrors SKIP_REASONS in documents/services/efolder_jobs.py.
export const SKIP_REASONS = [
  { value: "not_ours", label: "Not our client" },
  { value: "duplicate", label: "Already on file" },
  { value: "wrong_file", label: "Wrong file" },
  { value: "other", label: "Other" },
];

// Mirrors ACKNOWLEDGEMENTS in efolder_jobs.py: the 422 reason_code the
// confirm/reassign guardrail returns -> the acknowledge_warning that clears it.
export const ACKNOWLEDGEMENT_FOR_REASON = {
  closed_only: "closed",
  non_agency_only: "non_agency",
};

// Plain words for documents/services/efolder_match.py REASON_CODES.
export const REASON_TEXT = {
  auto_ok: "Matched with confidence",
  identical_zip_already_processed: "This exact zip was already processed",
  multiple_veterans: "Documents for more than one veteran in this zip",
  no_identity_match: "Could not find a client for these documents",
  no_text_layer: "The key documents have no readable text",
  weak_evidence: "Not enough agreeing documents to be sure",
  multiple_agency_matters: "This veteran has more than one Agency matter",
  closed_only: "Only a closed matter matched",
  non_agency_only: "Only a non-Agency matter matched (CAVC or other)",
  person_without_matter: "Found the person but they have no matter",
  zip_hint_conflict: "The zip name disagrees with the documents",
  identify_error: "Matching hit an error",
};

export const describeReasonCode = (code) => (code ? REASON_TEXT[code] || code.replace(/_/g, " ") : "");

// `stage` values the backend will never move away from on its own.
export const TERMINAL_STAGES = new Set(["done", "failed", "aborted"]);
export const isTerminalStage = (stage) => TERMINAL_STAGES.has(stage);

const data = (promise) => promise.then((response) => response.data);

/** `GET efolder/jobs/?batch=&status=&matter=&limit=&cursor=` -> `{ results, next }`. */
export const listEfolderJobs = async ({ batch, status, matter, limit, cursor } = {}) => {
  const params = {};
  if (batch) params.batch = batch;
  if (status) params.status = Array.isArray(status) ? status.join(",") : status;
  if (matter) params.matter = matter;
  if (limit) params.limit = limit;
  if (cursor) params.cursor = cursor;
  return data(api.get("/documents/efolder/jobs/", { params }));
};

/** `GET efolder/jobs/<id>/?members=skipped|failed|all&page=` -> payload (+ `members`). */
export const getEfolderJob = async (jobId, { members, page } = {}) => {
  const params = {};
  if (members) params.members = members;
  if (page) params.page = page;
  return data(api.get(`/documents/efolder/jobs/${jobId}/`, { params }));
};

const matterBody = (matterId, acknowledgeWarning) => ({
  matter_id: matterId,
  acknowledge_warning: acknowledgeWarning || null,
});

/**
 * `GET efolder/summary/` -> `{ firm: {...}, mine: {...} }`, each with
 * `needs_review, auto_matched_waiting, identifying, in_flight,
 * finished_last_24h, failed_last_24h, oldest_needs_review_at`.
 */
export const fetchEfolderSummary = async () => data(api.get("/documents/efolder/summary/"));

/** `POST efolder/jobs/<id>/confirm/` (needs_review -> queued). 422 carries `reason_code`. */
export const confirmEfolderJob = async (jobId, { matterId, acknowledgeWarning } = {}) =>
  data(api.post(`/documents/efolder/jobs/${jobId}/confirm/`, matterBody(matterId, acknowledgeWarning)));

/** `POST efolder/jobs/<id>/reassign/` (needs_review or queued). */
export const reassignEfolderJob = async (jobId, { matterId, acknowledgeWarning } = {}) =>
  data(api.post(`/documents/efolder/jobs/${jobId}/reassign/`, matterBody(matterId, acknowledgeWarning)));

/** `POST efolder/jobs/<id>/skip/` with one of SKIP_REASONS. */
export const skipEfolderJob = async (jobId, { reason, note } = {}) =>
  data(api.post(`/documents/efolder/jobs/${jobId}/skip/`, { reason, note: note || "" }));

/** `POST efolder/jobs/<id>/identify/` re-runs the match. */
export const retryEfolderIdentify = async (jobId) =>
  data(api.post(`/documents/efolder/jobs/${jobId}/identify/`));

/** `POST efolder/batches/<uuid>/confirm-auto/` -> `{ confirmed, skipped, results }`. */
export const confirmAutoMatchedBatch = async (batchId) =>
  data(api.post(`/documents/efolder/batches/${batchId}/confirm-auto/`));

const STATUS_MAX_IDS = 200;

/** `GET efolder/status/?matter_ids=a,b,c` -> per-matter freshness rows (<= 200 ids). */
export const fetchEfolderStatus = async (matterIds) => {
  const ids = (matterIds || []).map((id) => String(id).trim()).filter(Boolean);
  if (ids.length === 0) return [];
  const results = [];
  for (let start = 0; start < ids.length; start += STATUS_MAX_IDS) {
    const page = ids.slice(start, start + STATUS_MAX_IDS);
    const body = await data(
      api.get("/documents/efolder/status/", { params: { matter_ids: page.join(",") } })
    );
    results.push(...(body?.results || []));
  }
  return results;
};

// What an axios failure from the routes above means to the UI.
export const errorStatus = (error) => error?.response?.status ?? error?.status ?? null;
export const errorReasonCode = (error) => error?.response?.data?.reason_code ?? null;
export const errorMessage = (error, fallback = "Something went wrong. Please try again.") =>
  error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback;

// A 409 means someone else already acted; the body is the job's current payload.
export const conflictPayload = (error) =>
  errorStatus(error) === 409 && error?.response?.data?.job_id ? error.response.data : null;
